import { ComponentType } from 'react';

import {
  BRAND_AFFINITY,
  CAMPAIGN,
  CAMPAIGN_LABEL,
  EMAIL_CAMPAIGN,
  LIST_MEMBERSHIP,
  LIST_MEMBERSHIP_SINGLE,
  MESSAGE_CHANNEL,
  MESSAGE_TYPE,
  PHONE_COUNTRY,
  PHONE_TYPE,
  RECURRING_CAMPAIGN,
  SUBSCRIBED_MESSAGE_TYPES,
  TEMPLATE_ID,
  TRIGGER_WORKFLOW,
  UNSUBSCRIBED_CHANNEL,
  UNSUBSCRIBED_CHANNELS,
  UNSUBSCRIBED_MESSAGE_TYPE,
  UNSUBSCRIBED_MESSAGE_TYPES,
  USER_LIST_MEMBERSHIP,
  USER_LIST_MEMBERSHIP_SINGLE,
  WORKFLOW,
} from 'utils/fieldConstants';

import { BooleanSelect } from './boolean/BooleanSelect';
import { DateSelect } from './date/DateSelect';
import { SingleDoubleInput } from './double/SingleDoubleInput';
import { SingleLongInput } from './long/SingleLongInput';
import { SingleStringInput } from './string/SingleStringInput';
import { SingleListSelect } from './list/SingleListSelect';
import { SingleCampaignSelect } from './campaign/SingleCampaignSelect';
import { SingleChannelSelect } from './channel/SingleChannelSelect';
import { SingleMessageTypeSelect } from './messageType/SingleMessageTypeSelect';
import { SingleBrandAffinitySelect } from './brandAffinity/SingleBrandAffinitySelect';
import { SinglePhoneCountrySelect } from './phoneCountry/SinglePhoneCountrySelect';
import { SinglePhoneTypeSelect } from './phoneType/SinglePhoneTypeSelect';
import { SingleTemplateInput } from './template/SingleTemplateInput';
import { SingleJourneySelect } from './journey/SingleJourneySelect';
import {
  ValidatedMultiGeoLocationValueInputProps,
  ValidatedMultiStringValueInputProps,
  ValidatedSingleGeoLocationValueInputProps,
  ValidatedSingleStringValueInputProps,
} from './types';
import { CastType, FieldType } from '../../../types';
import { MultiBrandAffinitySelect } from './brandAffinity/MultiBrandAffinitySelect';
import { MultiPhoneCountrySelect } from './phoneCountry/MultiPhoneCountrySelect';
import { MultiPhoneTypeSelect } from './phoneType/MultiPhoneTypeSelect';
import { MultiListSelect } from './list/MultiListSelect';
import { MultiCampaignSelect } from './campaign/MultiCampaignSelect';
import { MultiChannelSelect } from './channel/MultiChannelSelect';
import { MultiMessageTypeSelect } from './messageType/MultiMessageTypeSelect';
import { MultiTemplateSelect } from './template/MultiTemplateSelect';
import { MultiJourneySelect } from './journey/MultiJourneySelect';
import { MultiStringInput } from './string/MultiStringInput';
import { MultiLongInput } from './long/MultiLongInput';
import { MultiDoubleInput } from './double/MultiDoubleInput';
import { SingleCampaignLabelSelect } from './campaignLabel/SingleCampaignLabelSelect';
import { MultiCampaignLabelSelect } from './campaignLabel/MultiCampaignLabelSelect';
import { SingleObjectInput } from './object/SingleObjectInput';
import { SingleGeoLocationInput } from './geolocation/SingleGeoLocationInput';
import { MultiGeoLocationInput } from './geolocation/MultiGeoLocationInput';

export const FIELD_TO_SINGLE_INPUT_MAP: Record<
  string,
  ComponentType<ValidatedSingleStringValueInputProps>
> = {
  // Brand Affinity
  [BRAND_AFFINITY]: SingleBrandAffinitySelect,

  // Phone Country
  [PHONE_COUNTRY]: SinglePhoneCountrySelect,

  // Phone Type
  [PHONE_TYPE]: SinglePhoneTypeSelect,

  // List Membership
  [USER_LIST_MEMBERSHIP_SINGLE]: SingleListSelect,
  [LIST_MEMBERSHIP_SINGLE]: SingleListSelect,
  [LIST_MEMBERSHIP]: SingleListSelect,
  [USER_LIST_MEMBERSHIP]: SingleListSelect,

  // Campaign
  [CAMPAIGN]: SingleCampaignSelect,
  [EMAIL_CAMPAIGN]: SingleCampaignSelect,
  [RECURRING_CAMPAIGN]: SingleCampaignSelect,

  // Campaign Label
  [CAMPAIGN_LABEL]: SingleCampaignLabelSelect,

  // Channel
  [MESSAGE_CHANNEL]: SingleChannelSelect,
  [UNSUBSCRIBED_CHANNELS]: SingleChannelSelect,
  [UNSUBSCRIBED_CHANNEL]: SingleChannelSelect,

  // Message Type
  [MESSAGE_TYPE]: SingleMessageTypeSelect,
  [SUBSCRIBED_MESSAGE_TYPES]: SingleMessageTypeSelect,
  [UNSUBSCRIBED_MESSAGE_TYPES]: SingleMessageTypeSelect,
  [UNSUBSCRIBED_MESSAGE_TYPE]: SingleMessageTypeSelect,

  // Template
  [TEMPLATE_ID]: SingleTemplateInput,

  // Journey
  [TRIGGER_WORKFLOW]: SingleJourneySelect,
  [WORKFLOW]: SingleJourneySelect,
};

export const FIELD_TO_MULTI_INPUT_MAP: Record<
  string,
  ComponentType<ValidatedMultiStringValueInputProps>
> = {
  // Brand Affinity
  [BRAND_AFFINITY]: MultiBrandAffinitySelect,

  // Phone Country
  [PHONE_COUNTRY]: MultiPhoneCountrySelect,

  // Phone Type
  [PHONE_TYPE]: MultiPhoneTypeSelect,

  // List Membership
  [USER_LIST_MEMBERSHIP_SINGLE]: MultiListSelect,
  [LIST_MEMBERSHIP_SINGLE]: MultiListSelect,
  [LIST_MEMBERSHIP]: MultiListSelect,
  [USER_LIST_MEMBERSHIP]: MultiListSelect,

  // Campaign
  [CAMPAIGN]: MultiCampaignSelect,
  [EMAIL_CAMPAIGN]: MultiCampaignSelect,
  [RECURRING_CAMPAIGN]: MultiCampaignSelect,

  // Campaign Label
  [CAMPAIGN_LABEL]: MultiCampaignLabelSelect,

  // Channel
  [MESSAGE_CHANNEL]: MultiChannelSelect,
  [UNSUBSCRIBED_CHANNELS]: MultiChannelSelect,
  [UNSUBSCRIBED_CHANNEL]: MultiChannelSelect,

  // Message Type
  [MESSAGE_TYPE]: MultiMessageTypeSelect,
  [SUBSCRIBED_MESSAGE_TYPES]: MultiMessageTypeSelect,
  [UNSUBSCRIBED_MESSAGE_TYPES]: MultiMessageTypeSelect,
  [UNSUBSCRIBED_MESSAGE_TYPE]: MultiMessageTypeSelect,

  // Template
  [TEMPLATE_ID]: MultiTemplateSelect,

  // Journey
  [TRIGGER_WORKFLOW]: MultiJourneySelect,
  [WORKFLOW]: MultiJourneySelect,
};

export const FIELD_TYPE_TO_SINGLE_INPUT_MAP: Record<
  string,
  | ComponentType<ValidatedSingleStringValueInputProps>
  | ComponentType<ValidatedSingleGeoLocationValueInputProps>
> = {
  // String
  [FieldType.KEYWORD]: SingleStringInput,
  [FieldType.STRING]: SingleStringInput,

  // Boolean
  [FieldType.BOOLEAN]: BooleanSelect,

  // Integer
  [FieldType.INTEGER]: SingleLongInput,

  // Float
  [FieldType.FLOAT]: SingleDoubleInput,

  // Date
  [FieldType.DATE]: DateSelect,

  // Obejct
  [FieldType.OBJECT]: SingleObjectInput,
  [FieldType.NESTED]: SingleObjectInput,

  // Geolocation
  [FieldType.GEO_LOCATION]: SingleGeoLocationInput,
};

export const FIELD_TYPE_TO_MULTI_INPUT_MAP: Record<
  string,
  | ComponentType<ValidatedMultiStringValueInputProps>
  | ComponentType<ValidatedMultiGeoLocationValueInputProps>
> = {
  // String
  [FieldType.KEYWORD]: MultiStringInput,
  [FieldType.STRING]: MultiStringInput,

  // Integer
  [FieldType.INTEGER]: MultiLongInput,

  // Float
  [FieldType.FLOAT]: MultiDoubleInput,

  // Geolocation
  [FieldType.GEO_LOCATION]: MultiGeoLocationInput,
};

export const CAST_TYPE_TO_SINGLE_INPUT_MAP: Record<
  string,
  ComponentType<ValidatedSingleStringValueInputProps>
> = {
  // Epoch Secs
  [CastType.EPOCHS_MILLIS]: DateSelect,

  // Epoch Mills
  [CastType.EPOCHS_SECS]: DateSelect,
};
